<template>
  <div class="shop-subcategory" :class="{ 'shop-subcategory--active': active }">
    <div class="shop-subcategory__title">{{ value.label }}</div>
    <ReadMore
      v-if="value.sublabel"
      :value="value.sublabel"
      :limit="2"
      :line-height="18"
      class="shop-subcategory__subtitle"
    />
    <div class="shop-subcategory__items">
      <template>
        <Product
          v-if="!items.filter(i => i.isDefault).length"
          :selected="!selected"
          :value="{
            product: {
              name: $t('noShopDefaultProduct'),
            },
            isDummy: true,
            isDefault: true,
          }"
          :readonly="readonly"
          :active="active"
          class="shop-subcategory__item"
          @click="selected && $emit('unselect', selected.id)"
        />
        <Product
          v-for="(item, index) in items"
          :key="index"
          :selected="item.selected"
          :value="item"
          :readonly="readonly"
          :active="active"
          :currency="shop.metadata.currency"
          class="shop-subcategory__item"
          @click="() => onClick(item)"
          @quantity="qty => $emit('select', item.id, qty)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import Product from '../Product/Card'
import Quantity from '../components/Quantity.vue'
import ReadMore from '../components/ReadMore.vue'
import { CheckConditions } from '../utils'

export default {
  inject: ['shop', 'bus'],
  props: {
    value: Object,
    active: Boolean,
    category: Object,
    readonly: Boolean,
  },
  watch: {
    active(val) {
      if (!val) return

      this.$el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    },
  },
  computed: {
    selected() {
      return this.items.find(i => i.selected)
    },
    items() {
      return this.value.items
        .filter(v => CheckConditions(v, this.shop))
        .sort((a, b) => {
          if (a.isDefault) return -1
          if (b.isDefault) return 1

          return 0
        })
    },
  },
  methods: {
    onClick(item) {
      const hasContent = [
        'product.attachments.length',
        'product.images.length',
        'product.technicalDescription',
        'product.description',
        'product.variants.items.length',
        'referencePropertyChanges.length',
        'sets.length',
        'quantity.enabled',
        'product.products.length',
      ].some(key => this.$path(key, item))

      if (window.innerWidth < 900 && hasContent) {
        const quantity = item.quantity

        return this.bus.$emit('product', {
          type: 'select',
          data: item,
          actions: [
            {
              value: {
                ...item,
                quantity,
              },
              component: Quantity,
              listeners: {
                change: q => (quantity.value = q),
              },
            },
            {
              label: this.$t('select'),
              icon: ['fal', 'angle-right'],
              theme: 'primary',
              listeners: {
                click: () => {
                  this.$emit('select', item.id, quantity.value || 1)
                  this.bus.$emit('product:close')
                },
              },
            },
          ],
        })
      }

      this.$emit('select', item.id)
    },
  },
  metaInfo() {
    let preload = []
    if (this.active)
      preload.push(
        ...(this.$path('value.items') || [])
          .map(item => {
            if (!item.images) return
            return item.images.map(i => ({ rel: 'preload', as: 'image', href: i.url }))
          })
          .filter(Boolean)
          .flat(),
      )
    return {
      link: [...preload],
    }
  },
  components: {
    Product,
    ReadMore,
  },
}
</script>

<style lang="scss">
@import '../mixins.scss';

.shop-subcategory {
  position: relative;
  // padding: 1rem 2.5rem;

  &--active,
  &:hover {
    .shop-subcategory__items {
    }
  }

  &--active {
    .shop-subcategory__title {
      color: var(--primary);
    }
  }

  &__title {
    font-weight: bold;
    font-size: 1rem;
  }

  &__subtitle {
    font-size: 0.8rem;
    color: rgba(black, 0.7);
    letter-spacing: 0.2px;
    margin-top: 0.25rem;
  }

  &__items {
    display: grid;

    transition: opacity 150ms ease-out;
    margin-top: 0.75rem;
  }

  &__item {
    --selected-border-color: #{rgba(black, 0.3)};
    --active-border-color: #{rgba(black, 0.5)};
    --hover-border-color: #{rgba(black, 0.2)};

    padding: 1rem;
    border: 1px solid var(--border-color);

    &:first-child {
      border-top-left-radius: calc(var(--border-radius) * 2);
      border-top-right-radius: calc(var(--border-radius) * 2);
    }

    &:last-child {
      border-bottom-left-radius: calc(var(--border-radius) * 2);
      border-bottom-right-radius: calc(var(--border-radius) * 2);
    }

    &:nth-last-child(n + 2) {
      border-bottom: none;
    }

    &.shop-product-card--selected {
      border-color: var(--selected-border-color) !important;

      & + * {
        border-top-color: var(--selected-border-color) !important;
      }

      &.shop-product-card--active {
        background-color: css-alpha('primary', 5%);
        border-color: var(--active-border-color) !important;

        & + * {
          border-top-color: var(--active-border-color) !important;
        }

        .shop-product-card__name {
          font-weight: bold;
        }
      }
    }

    &:hover {
      border-color: var(--hover-border-color);

      & + * {
        border-top-color: var(--hover-border-color);
      }
    }

    &:focus-visible {
      outline: 3px solid lightgrey !important;
      outline-offset: 2px;
    }

    .shop-product-card__image {
      border: none;
    }
  }

  @include onPhone {
    .shop-product-quantity {
      font-size: 0.8em;
      flex-shrink: 0;
      pointer-events: none;

      input {
        background-color: transparent;
        width: 3ch;
      }

      button {
        display: none;
      }

      .elder-input__field {
        border: none;
        background-color: #f2f2f2;
      }

      .elder-input__suffix {
        display: flex !important;
        padding-left: 0;
        border-left: none;
      }
    }
  }
}
</style>
